import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Rating,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import { FeedbackService } from '../../services/feedbackService';
import { useAuth } from '../../contexts/AuthContext';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

/**
 * Composant pour afficher les statistiques de feedback pour un service technique
 */
const FeedbackStats = ({ technicalServiceId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Si aucun ID de service technique n'est fourni, utiliser l'ID de l'utilisateur courant
        const serviceId = technicalServiceId || currentUser?.uid;
        
        if (!serviceId) {
          setError('Impossible de récupérer les statistiques : ID de service manquant');
          setLoading(false);
          return;
        }
        
        const feedbackStats = await FeedbackService.getFeedbackStatsForTechnicalService(serviceId);
        setStats(feedbackStats);
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques de feedback:', error);
        setError('Erreur lors de la récupération des statistiques');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [technicalServiceId, currentUser]);
  
  // Fonction pour calculer le pourcentage d'une note
  const calculateRatingPercentage = (ratingCount) => {
    if (!stats || stats.totalFeedbacks === 0) return 0;
    return (ratingCount / stats.totalFeedbacks) * 100;
  };
  
  // Fonction pour calculer le pourcentage d'un critère spécifique
  const calculateDetailPercentage = (detailType, value) => {
    if (!stats || !stats.detailsAverages[detailType]) return 0;
    
    const total = Object.values(stats.detailsAverages[detailType]).reduce((sum, count) => sum + count, 0);
    if (total === 0) return 0;
    
    return (stats.detailsAverages[detailType][value] / total) * 100;
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }
  
  if (!stats || stats.totalFeedbacks === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Aucune évaluation disponible pour le moment.
      </Alert>
    );
  }
  
  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Statistiques des évaluations
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ mr: 2 }}>
          <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
            {stats.averageRating}
          </Typography>
          <Rating 
            value={stats.averageRating} 
            precision={0.1} 
            readOnly 
            size="small"
          />
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Basé sur {stats.totalFeedbacks} évaluation{stats.totalFeedbacks > 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle1" gutterBottom>
        Distribution des notes
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        {[5, 4, 3, 2, 1].map((rating) => (
          <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 30 }}>
              {rating}
            </Typography>
            <StarIcon sx={{ color: 'gold', fontSize: 16, mr: 1 }} />
            <Box sx={{ flexGrow: 1, mr: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={calculateRatingPercentage(stats.ratingDistribution[rating])} 
                sx={{ 
                  height: 8, 
                  borderRadius: 1,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: rating >= 4 ? 'success.main' : (rating >= 3 ? 'warning.main' : 'error.main')
                  }
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {stats.ratingDistribution[rating]}
            </Typography>
          </Box>
        ))}
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle1" gutterBottom>
        Critères spécifiques
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon sx={{ mr: 1, fontSize: 18 }} /> Respect des délais
            </Typography>
            
            {['excellent', 'good', 'average', 'poor'].map((value) => (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Chip 
                  label={value === 'excellent' ? 'Excellent' : (value === 'good' ? 'Bon' : (value === 'average' ? 'Moyen' : 'À améliorer'))}
                  size="small"
                  sx={{ 
                    minWidth: 80, 
                    mr: 1,
                    backgroundColor: value === 'excellent' ? 'success.light' : (value === 'good' ? 'info.light' : (value === 'average' ? 'warning.light' : 'error.light')),
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
                <Box sx={{ flexGrow: 1, mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateDetailPercentage('timeliness', value)} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 1,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: value === 'excellent' ? 'success.main' : (value === 'good' ? 'info.main' : (value === 'average' ? 'warning.main' : 'error.main'))
                      }
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.detailsAverages.timeliness[value]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EngineeringIcon sx={{ mr: 1, fontSize: 18 }} /> Qualité de l'intervention
            </Typography>
            
            {['excellent', 'good', 'average', 'poor'].map((value) => (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Chip 
                  label={value === 'excellent' ? 'Excellent' : (value === 'good' ? 'Bon' : (value === 'average' ? 'Moyen' : 'À améliorer'))}
                  size="small"
                  sx={{ 
                    minWidth: 80, 
                    mr: 1,
                    backgroundColor: value === 'excellent' ? 'success.light' : (value === 'good' ? 'info.light' : (value === 'average' ? 'warning.light' : 'error.light')),
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
                <Box sx={{ flexGrow: 1, mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateDetailPercentage('quality', value)} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 1,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: value === 'excellent' ? 'success.main' : (value === 'good' ? 'info.main' : (value === 'average' ? 'warning.main' : 'error.main'))
                      }
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.detailsAverages.quality[value]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ThumbUpIcon sx={{ mr: 1, fontSize: 18 }} /> Communication
            </Typography>
            
            {['excellent', 'good', 'average', 'poor'].map((value) => (
              <Box key={value} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Chip 
                  label={value === 'excellent' ? 'Excellent' : (value === 'good' ? 'Bon' : (value === 'average' ? 'Moyen' : 'À améliorer'))}
                  size="small"
                  sx={{ 
                    minWidth: 80, 
                    mr: 1,
                    backgroundColor: value === 'excellent' ? 'success.light' : (value === 'good' ? 'info.light' : (value === 'average' ? 'warning.light' : 'error.light')),
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
                <Box sx={{ flexGrow: 1, mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateDetailPercentage('communication', value)} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 1,
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: value === 'excellent' ? 'success.main' : (value === 'good' ? 'info.main' : (value === 'average' ? 'warning.main' : 'error.main'))
                      }
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.detailsAverages.communication[value]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FeedbackStats;
